import {
  ProjectId,
  ThreadId,
  type AssistantDeliveryMode,
  type ClaudeCodeEffort,
  type ComposerQueueEnqueueInput,
  type ComposerQueuedMessage,
  type ModelSelection,
  type ProviderInteractionMode,
  type ProviderKind,
  type ProviderMentionReference,
  type ProviderPluginDescriptor,
  type ProviderSkillReference,
  type ProviderStartOptions,
  type RuntimeMode,
  type ServerProviderAuthStatus,
  type SourceProposedPlanReference,
  type ThreadId as ThreadIdType,
  type UploadChatAttachment,
} from "@taskin-code/contracts";
import { sanitizeBranchFragment } from "@taskin-code/shared/git";
import { applyClaudePromptEffortPrefix, getModelCapabilities } from "@taskin-code/shared/model";
import { isGenericTerminalThreadTitle } from "@taskin-code/shared/terminalThreads";
import {
  type ChatMessage,
  type SessionPhase,
  type Thread,
  type ThreadPrimarySurface,
} from "../types";
import { type ComposerAttachment, type DraftThreadState } from "../composerDraftStore";
import { Schema } from "effect";
import {
  appendQuotedContextsToPrompt,
  filterQuotedContextsWithText,
  formatQuotedContextLabel,
  stripInlineQuotedContextPlaceholders,
  type QuotedContextDraft,
} from "../lib/quotedContext";
import { attachmentOnlyBootstrapPrompt } from "../lib/displayedUserMessage";
import { resolveSubagentPresentationForThread } from "../lib/subagentPresentation";
import { type WorkLogEntry, type WorkLogSubagentTaskThreadLink } from "../session-logic";
import { localSubagentThreadId } from "./ChatView.selectors";

export const LAST_INVOKED_SCRIPT_BY_PROJECT_KEY = "t3code:last-invoked-script-by-project";
const WORKTREE_NAME_PREFIX = "taskin";

export const LastInvokedScriptByProjectSchema = Schema.Record(ProjectId, Schema.String);

export function buildLocalDraftThread(
  threadId: ThreadId,
  draftThread: DraftThreadState,
  fallbackModelSelection: ModelSelection,
  error: string | null,
): Thread {
  return {
    id: threadId,
    codexThreadId: null,
    projectId: draftThread.projectId,
    title: draftThread.entryPoint === "terminal" ? "New terminal" : "New thread",
    modelSelection: fallbackModelSelection,
    runtimeMode: draftThread.runtimeMode,
    interactionMode: draftThread.interactionMode,
    session: null,
    messages: [],
    error,
    createdAt: draftThread.createdAt,
    latestTurn: null,
    lastVisitedAt: draftThread.createdAt,
    envMode: draftThread.envMode,
    branch: draftThread.branch,
    worktreePath: draftThread.worktreePath,
    handoff: null,
    turnDiffSummaries: [],
    activities: [],
    proposedPlans: [],
  };
}

/**
 * The model selection to persist on `thread.create` when a local draft is
 * promoted to a real thread.
 *
 * Rebuilt rather than passed through because the model may need a fallback,
 * but every other field of the composer's selection must survive the rebuild.
 * `agentId` in particular: the thread row is what the picker derives its state
 * from after the send, and a `thread.create` that drops the agent makes the
 * *next* turn run as the bare model — restarting the provider session and
 * losing the conversation so far. (Same trap as `normalizeModelSelection` in
 * composerDraftStore: hand-rebuilding a ModelSelection silently strips fields.)
 */
export function buildThreadCreateModelSelection(
  selection: ModelSelection,
  model: string,
): ModelSelection {
  return { ...selection, model };
}

export function revokeBlobPreviewUrl(previewUrl: string | undefined): void {
  if (!previewUrl || typeof URL === "undefined" || !previewUrl.startsWith("blob:")) {
    return;
  }
  URL.revokeObjectURL(previewUrl);
}

export function revokeUserMessagePreviewUrls(message: ChatMessage): void {
  if (message.role !== "user" || !message.attachments) {
    return;
  }
  // Every kind: a staged file holds an object URL for its download link just
  // like an image holds one for its thumbnail, and leaking it leaks the File.
  for (const attachment of message.attachments) {
    revokeBlobPreviewUrl(attachment.previewUrl);
  }
}

/**
 * The blob preview URLs an optimistic user message is holding, by attachment id.
 *
 * Keyed rather than positional: the server copy of the message and the
 * optimistic one agree on ids but need not agree on order, and once a message
 * can mix images with files a positional hand-off would hang one attachment's
 * URL on another attachment.
 */
export function collectUserMessageBlobPreviewUrls(message: ChatMessage): Record<string, string> {
  if (message.role !== "user" || !message.attachments) {
    return {};
  }
  const previewUrlByAttachmentId: Record<string, string> = {};
  for (const attachment of message.attachments) {
    if (!attachment.previewUrl || !attachment.previewUrl.startsWith("blob:")) continue;
    previewUrlByAttachmentId[attachment.id] = attachment.previewUrl;
  }
  return previewUrlByAttachmentId;
}

export function appendVoiceTranscriptToPrompt(
  currentPrompt: string,
  transcript: string,
): string | null {
  const trimmedTranscript = transcript.trim();
  if (trimmedTranscript.length === 0) {
    return null;
  }
  return currentPrompt.trim().length === 0
    ? trimmedTranscript
    : `${currentPrompt.replace(/\s+$/, "")}\n${trimmedTranscript}`;
}

export function sanitizeVoiceErrorMessage(message: string): string {
  const normalized = message.trim();
  if (normalized.length === 0) {
    return "The voice note could not be transcribed.";
  }

  const firstLine = normalized.split("\n")[0]?.trim() ?? normalized;
  const withoutInlineStack = firstLine.replace(/\s+at file:\/\/.*$/s, "").trim();
  const withoutRemoteMethodPrefix = withoutInlineStack.replace(
    /^Error invoking remote method ['"][^'"]+['"]:\s*/i,
    "",
  );
  const withoutRepeatedErrorPrefix = withoutRemoteMethodPrefix.replace(/^(Error:\s*)+/i, "").trim();

  return withoutRepeatedErrorPrefix.length > 0
    ? withoutRepeatedErrorPrefix
    : "The voice note could not be transcribed.";
}

export function isVoiceAuthExpiredMessage(message: string): boolean {
  const normalized = message.toLowerCase();
  return normalized.includes("chatgpt login has expired") || normalized.includes("sign in again");
}

export function describeVoiceRecordingStartError(error: unknown): string {
  if (!(error instanceof Error)) {
    return "The microphone could not be opened.";
  }

  const normalizedMessage = error.message.trim();
  const errorName = typeof error.name === "string" ? error.name : "";

  if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
    return "Microphone access was denied. Enable it in macOS Privacy & Security > Microphone for Taskin Code, then try again.";
  }
  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return "No microphone was found. Connect one and try again.";
  }
  if (errorName === "NotReadableError" || errorName === "TrackStartError") {
    return "The microphone is busy or unavailable right now. Close other audio apps and try again.";
  }
  if (errorName === "SecurityError") {
    return "Microphone access is blocked in this environment.";
  }
  if (normalizedMessage.length > 0) {
    return sanitizeVoiceErrorMessage(normalizedMessage);
  }

  return "The microphone could not be opened.";
}

export function deriveComposerVoiceState(input: {
  authStatus: ServerProviderAuthStatus | null | undefined;
  voiceTranscriptionAvailable: boolean | undefined;
  isRecording: boolean;
  isTranscribing: boolean;
}): {
  canRenderVoiceNotes: boolean;
  canStartVoiceNotes: boolean;
  showVoiceNotesControl: boolean;
} {
  const canRenderVoiceNotes = input.authStatus !== "unauthenticated";
  const canStartVoiceNotes = canRenderVoiceNotes && input.voiceTranscriptionAvailable !== false;

  return {
    canRenderVoiceNotes,
    canStartVoiceNotes,
    showVoiceNotesControl: canRenderVoiceNotes || input.isRecording || input.isTranscribing,
  };
}

export interface LocalDispatchSnapshot {
  startedAt: string;
  preparingWorktree: boolean;
  latestTurnTurnId: Thread["latestTurn"] extends infer T
    ? T extends { turnId: infer U }
      ? U | null
      : null
    : null;
  latestTurnRequestedAt: string | null;
  latestTurnStartedAt: string | null;
  latestTurnCompletedAt: string | null;
  sessionOrchestrationStatus: Thread["session"] extends infer T
    ? T extends { orchestrationStatus: infer U }
      ? U | null
      : null
    : null;
  sessionUpdatedAt: string | null;
}

export function createLocalDispatchSnapshot(
  activeThread: Thread | undefined,
  options?: { preparingWorktree?: boolean },
): LocalDispatchSnapshot {
  const latestTurn = activeThread?.latestTurn ?? null;
  const session = activeThread?.session ?? null;
  return {
    startedAt: new Date().toISOString(),
    preparingWorktree: Boolean(options?.preparingWorktree),
    latestTurnTurnId: latestTurn?.turnId ?? null,
    latestTurnRequestedAt: latestTurn?.requestedAt ?? null,
    latestTurnStartedAt: latestTurn?.startedAt ?? null,
    latestTurnCompletedAt: latestTurn?.completedAt ?? null,
    sessionOrchestrationStatus: session?.orchestrationStatus ?? null,
    sessionUpdatedAt: session?.updatedAt ?? null,
  };
}

export function hasServerAcknowledgedLocalDispatch(input: {
  localDispatch: LocalDispatchSnapshot | null;
  phase: SessionPhase;
  latestTurn: Thread["latestTurn"] | null;
  session: Thread["session"] | null;
  hasPendingApproval: boolean;
  hasPendingUserInput: boolean;
  threadError: string | null | undefined;
}): boolean {
  if (!input.localDispatch) {
    return false;
  }
  if (
    input.phase === "running" ||
    input.hasPendingApproval ||
    input.hasPendingUserInput ||
    Boolean(input.threadError)
  ) {
    return true;
  }

  const latestTurn = input.latestTurn ?? null;
  const session = input.session ?? null;
  const nextSessionOrchestrationStatus = session?.orchestrationStatus ?? null;
  const latestTurnChanged =
    input.localDispatch.latestTurnTurnId !== (latestTurn?.turnId ?? null) ||
    input.localDispatch.latestTurnRequestedAt !== (latestTurn?.requestedAt ?? null) ||
    input.localDispatch.latestTurnStartedAt !== (latestTurn?.startedAt ?? null) ||
    input.localDispatch.latestTurnCompletedAt !== (latestTurn?.completedAt ?? null);

  if (latestTurnChanged) {
    return true;
  }

  if (input.localDispatch.sessionOrchestrationStatus !== nextSessionOrchestrationStatus) {
    if (
      input.localDispatch.sessionOrchestrationStatus === null &&
      nextSessionOrchestrationStatus === "ready"
    ) {
      return false;
    }
    return true;
  }

  return false;
}

export const ACTIVE_TURN_LAYOUT_SETTLE_DELAY_MS = 180;

export function shouldStartActiveTurnLayoutGrace(options: {
  previousTurnLayoutLive: boolean;
  currentTurnLayoutLive: boolean;
  latestTurnStartedAt: string | null;
}): boolean {
  return (
    options.previousTurnLayoutLive &&
    !options.currentTurnLayoutLive &&
    options.latestTurnStartedAt !== null
  );
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Could not read image data."));
    });
    reader.addEventListener("error", () => {
      reject(reader.error ?? new Error("Failed to read image."));
    });
    reader.readAsDataURL(file);
  });
}

export function buildSuggestedWorktreeName(input: {
  associatedWorktreeBranch?: string | null;
  title?: string | null;
}): string {
  const normalizedExisting =
    input.associatedWorktreeBranch?.trim().replace(/^(codex|t3code|dpcode)\//i, "") ?? "";
  const preferred =
    normalizedExisting ||
    `${WORKTREE_NAME_PREFIX}/${sanitizeBranchFragment(input.title ?? "update")}`;
  const normalized = preferred.toLowerCase();
  return normalized.startsWith(`${WORKTREE_NAME_PREFIX}/`)
    ? normalized
    : `${WORKTREE_NAME_PREFIX}/${sanitizeBranchFragment(normalized)}`;
}

export function cloneComposerImageForRetry(image: ComposerAttachment): ComposerAttachment {
  if (typeof URL === "undefined" || !image.previewUrl.startsWith("blob:")) {
    return image;
  }
  try {
    return {
      ...image,
      previewUrl: URL.createObjectURL(image.file),
    };
  } catch {
    return image;
  }
}

export function deriveComposerSendState(options: {
  prompt: string;
  imageCount: number;
  quotedContexts: ReadonlyArray<QuotedContextDraft>;
}): {
  trimmedPrompt: string;
  sendableQuotedContexts: QuotedContextDraft[];
  expiredTerminalContextCount: number;
  hasSendableContent: boolean;
} {
  const trimmedPrompt = stripInlineQuotedContextPlaceholders(options.prompt).trim();
  const sendableQuotedContexts = filterQuotedContextsWithText(options.quotedContexts);
  const expiredTerminalContextCount = options.quotedContexts.length - sendableQuotedContexts.length;
  return {
    trimmedPrompt,
    sendableQuotedContexts,
    expiredTerminalContextCount,
    hasSendableContent:
      trimmedPrompt.length > 0 || options.imageCount > 0 || sendableQuotedContexts.length > 0,
  };
}

export function buildExpiredTerminalContextToastCopy(
  expiredTerminalContextCount: number,
  variant: "omitted" | "empty",
): { title: string; description: string } {
  const count = Math.max(1, Math.floor(expiredTerminalContextCount));
  const noun = count === 1 ? "Expired terminal context" : "Expired terminal contexts";
  if (variant === "empty") {
    return {
      title: `${noun} won't be sent`,
      description: "Remove it or re-add it to include terminal output.",
    };
  }
  return {
    title: `${noun} omitted from message`,
    description: "Re-add it if you want that terminal output included.",
  };
}

export function shouldRenderTerminalWorkspace(options: {
  activeProjectExists: boolean;
  presentationMode: "drawer" | "workspace";
  terminalOpen: boolean;
}): boolean {
  return (
    options.terminalOpen && options.presentationMode === "workspace" && options.activeProjectExists
  );
}

export function shouldAutoDeleteTerminalThreadOnLastClose(options: {
  isLastTerminal: boolean;
  isServerThread: boolean;
  terminalEntryPoint: ThreadPrimarySurface;
  thread:
    | Pick<Thread, "activities" | "latestTurn" | "messages" | "proposedPlans" | "session" | "title">
    | null
    | undefined;
}): boolean {
  const { thread } = options;
  if (
    !options.isLastTerminal ||
    !options.isServerThread ||
    options.terminalEntryPoint !== "terminal" ||
    !thread
  ) {
    return false;
  }
  return (
    isGenericTerminalThreadTitle(thread.title) &&
    thread.messages.length === 0 &&
    thread.latestTurn === null &&
    thread.session === null &&
    thread.activities.length === 0 &&
    thread.proposedPlans.length === 0
  );
}

export interface ThreadBreadcrumb {
  threadId: ThreadIdType;
  title: string;
}

export function buildThreadBreadcrumbs(
  threads: ReadonlyArray<Thread>,
  thread: Pick<Thread, "id" | "parentThreadId"> | null | undefined,
): ThreadBreadcrumb[] {
  if (!thread?.parentThreadId) {
    return [];
  }

  const threadById = new Map(threads.map((entry) => [entry.id, entry] as const));
  const breadcrumbs: ThreadBreadcrumb[] = [];
  const visited = new Set<ThreadIdType>();
  let currentParentId: ThreadIdType | null = thread.parentThreadId ?? null;

  while (currentParentId && !visited.has(currentParentId)) {
    visited.add(currentParentId);
    const parentThread = threadById.get(currentParentId);
    if (!parentThread) {
      break;
    }
    breadcrumbs.unshift({
      threadId: parentThread.id,
      title: parentThread.parentThreadId
        ? resolveSubagentPresentationForThread({ thread: parentThread, threads }).fullLabel
        : parentThread.title,
    });
    currentParentId = parentThread.parentThreadId ?? null;
  }

  return breadcrumbs;
}

export interface ThreadPlaneIssueBadge {
  readonly ref: string;
  /** True when the ref came from an ancestor, not this thread's own link. */
  readonly inherited: boolean;
}

/**
 * The issue a thread's header badge shows — its own link, or the nearest
 * ancestor's when it has none.
 *
 * Mirrors the server's `resolveThreadPlaneIssueFromRow`: a delegate whose
 * system prompt names an inherited issue shows that same issue here, so what
 * the model was told and what the person sees never disagree. `lineage` is
 * root-first ending in the thread itself — the same array
 * `buildThreadBreadcrumbs` walks — so no separate parent-chain traversal is
 * needed on top of what breadcrumbs already compute.
 */
export function deriveThreadPlaneIssue(
  lineage: ReadonlyArray<Pick<Thread, "planeIssueRef">>,
): ThreadPlaneIssueBadge | null {
  for (let index = lineage.length - 1; index >= 0; index -= 1) {
    const ref = lineage[index]?.planeIssueRef;
    if (ref) {
      return { ref, inherited: index !== lineage.length - 1 };
    }
  }
  return null;
}

/**
 * Turns the provider thread ids a child-thread subagent row carries into links
 * the panel can offer. Only threads that are actually loaded produce a link, so
 * "Open thread" never navigates into nothing.
 */
function resolveSubagentTaskThreadLinks(input: {
  subagentTask: NonNullable<WorkLogEntry["subagentTask"]>;
  parentThreadId: ThreadIdType | null;
  threadById: ReadonlyMap<ThreadIdType, Thread>;
  threads: ReadonlyArray<Thread>;
}): WorkLogSubagentTaskThreadLink[] {
  const receiverProviderThreadIds = input.subagentTask.receiverProviderThreadIds ?? [];
  if (receiverProviderThreadIds.length === 0) {
    return [];
  }

  const receiverByProviderThreadId = new Map(
    (input.subagentTask.receivers ?? []).map(
      (receiver) => [receiver.providerThreadId, receiver] as const,
    ),
  );

  const links: WorkLogSubagentTaskThreadLink[] = [];
  for (const providerThreadId of receiverProviderThreadIds) {
    const matchedThread =
      input.threadById.get(ThreadId.makeUnsafe(providerThreadId)) ??
      (input.parentThreadId
        ? input.threadById.get(localSubagentThreadId(input.parentThreadId, providerThreadId))
        : undefined);
    if (!matchedThread) {
      continue;
    }
    const receiver = receiverByProviderThreadId.get(providerThreadId);
    const label =
      receiver?.nickname ??
      resolveSubagentPresentationForThread({ thread: matchedThread, threads: input.threads })
        .primaryLabel ??
      matchedThread.title;
    links.push({ threadId: matchedThread.id, label });
  }
  return links;
}

/**
 * The child thread mirroring an in-session subagent's transcript (Claude). Its
 * local id is minted from the `Task` tool_use id, so the link appears only once
 * the adapter published that id and the thread itself has been loaded.
 */
function resolveInSessionSubagentThreadLinks(input: {
  subagentTask: NonNullable<WorkLogEntry["subagentTask"]>;
  parentThreadId: ThreadIdType | null;
  threadById: ReadonlyMap<ThreadIdType, Thread>;
  threads: ReadonlyArray<Thread>;
}): WorkLogSubagentTaskThreadLink[] {
  const taskToolUseId = input.subagentTask.taskToolUseId;
  if (!taskToolUseId || !input.parentThreadId) {
    return [];
  }
  const matchedThread = input.threadById.get(
    localSubagentThreadId(input.parentThreadId, taskToolUseId),
  );
  if (!matchedThread) {
    return [];
  }
  const label =
    resolveSubagentPresentationForThread({ thread: matchedThread, threads: input.threads })
      .primaryLabel ?? matchedThread.title;
  return [{ threadId: matchedThread.id, label }];
}

export function enrichSubagentWorkEntries(
  workEntries: ReadonlyArray<WorkLogEntry>,
  threads: ReadonlyArray<Thread>,
  parentThreadId: ThreadIdType | null,
): WorkLogEntry[] {
  if (workEntries.length === 0) {
    return [];
  }

  const threadById = new Map(threads.map((thread) => [thread.id, thread] as const));

  return workEntries.map((entry) => {
    if (entry.subagentTask) {
      const resolve =
        entry.subagentTask.kind === "child-thread"
          ? resolveSubagentTaskThreadLinks
          : resolveInSessionSubagentThreadLinks;
      const threadLinks = resolve({
        subagentTask: entry.subagentTask,
        parentThreadId,
        threadById,
        threads,
      });
      return threadLinks.length > 0
        ? { ...entry, subagentTask: { ...entry.subagentTask, threadLinks } }
        : entry;
    }
    return entry;
  });
}

// ── Composer queue ───────────────────────────────────────────────────
//
// A queued message carries two snapshots: `turn`, the final text and options the server dispatches
// verbatim, and `composer`, the raw state "Edit" puts back. The formatting lives here rather than
// on the server because the effort prefix, the quoted-context blocks and the image-only bootstrap
// prompt are all web concerns; the server must never re-derive them.

export interface ComposerPluginSuggestion {
  plugin: ProviderPluginDescriptor;
  mention: ProviderMentionReference;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** `/` for Claude, `$` for Codex — how a skill is written inline in the prompt. */
export function skillMentionPrefix(provider: string): string {
  return provider === "claudeAgent" ? "/" : "$";
}

export function promptIncludesSkillMention(
  prompt: string,
  skillName: string,
  provider: string,
): boolean {
  const prefix = escapeRegExp(skillMentionPrefix(provider));
  const pattern = new RegExp(`(^|\\s)${prefix}${escapeRegExp(skillName)}(?=\\s|$)`, "i");
  return pattern.test(prompt);
}

function collectPromptMentionNames(prompt: string): string[] {
  const names: string[] = [];
  for (const match of prompt.matchAll(/(^|\s)@([^\s@]+)(?=\s|$)/g)) {
    const mentionName = (match[2] ?? "").trim();
    if (mentionName.length > 0) {
      names.push(mentionName);
    }
  }
  return names;
}

function normalizeMentionNameKey(name: string): string {
  return name.trim().toLowerCase();
}

export function resolvePromptPluginMentions(params: {
  prompt: string;
  existingMentions: ReadonlyArray<ProviderMentionReference>;
  providerPlugins: ReadonlyArray<ComposerPluginSuggestion>;
}): ProviderMentionReference[] {
  const promptMentionNames = collectPromptMentionNames(params.prompt);
  if (promptMentionNames.length === 0) {
    return [];
  }

  const uniquePromptMentionNames: string[] = [];
  const seenPromptMentionNames = new Set<string>();
  for (const mentionName of promptMentionNames) {
    const key = normalizeMentionNameKey(mentionName);
    if (seenPromptMentionNames.has(key)) {
      continue;
    }
    seenPromptMentionNames.add(key);
    uniquePromptMentionNames.push(mentionName);
  }

  const existingMentionsByName = new Map<string, ProviderMentionReference[]>();
  for (const mention of params.existingMentions) {
    const key = normalizeMentionNameKey(mention.name);
    const bucket = existingMentionsByName.get(key);
    if (bucket) {
      bucket.push(mention);
    } else {
      existingMentionsByName.set(key, [mention]);
    }
  }

  const providerMentionsByName = new Map<string, ProviderMentionReference[]>();
  for (const suggestion of params.providerPlugins) {
    const key = normalizeMentionNameKey(suggestion.plugin.name);
    const bucket = providerMentionsByName.get(key);
    if (bucket) {
      bucket.push(suggestion.mention);
    } else {
      providerMentionsByName.set(key, [suggestion.mention]);
    }
  }

  const resolvedMentions: ProviderMentionReference[] = [];
  const seenPaths = new Set<string>();

  for (const mentionName of uniquePromptMentionNames) {
    const key = normalizeMentionNameKey(mentionName);
    const existingMention = (existingMentionsByName.get(key) ?? []).find(
      (candidate) => !seenPaths.has(candidate.path),
    );
    if (existingMention) {
      seenPaths.add(existingMention.path);
      resolvedMentions.push(existingMention);
      continue;
    }

    const discoveredMentions = providerMentionsByName.get(key) ?? [];
    if (discoveredMentions.length === 1) {
      const discoveredMention = discoveredMentions[0]!;
      seenPaths.add(discoveredMention.path);
      resolvedMentions.push(discoveredMention);
    }
  }

  return resolvedMentions;
}

/** Applies the provider's prompt-injected effort prefix, when the model wants one. */
export function formatOutgoingPrompt(params: {
  provider: ProviderKind;
  model: string | null;
  effort: string | null;
  text: string;
}): string {
  const caps = getModelCapabilities(params.provider, params.model);
  if (params.effort && caps.promptInjectedEffortLevels.includes(params.effort)) {
    return applyClaudePromptEffortPrefix(params.text, params.effort as ClaudeCodeEffort | null);
  }
  return params.text;
}

/** One line for the queue row: the prompt, or whatever else the message actually carries. */
export function buildQueuedComposerPreviewText(input: {
  trimmedPrompt: string;
  attachments: ReadonlyArray<{ name: string }>;
  quotedContexts: ReadonlyArray<QuotedContextDraft>;
}): string {
  if (input.trimmedPrompt.length > 0) {
    return input.trimmedPrompt;
  }
  const firstAttachment = input.attachments[0];
  if (firstAttachment) {
    return `Attachment: ${firstAttachment.name}`;
  }
  const firstQuotedContext = input.quotedContexts[0];
  if (firstQuotedContext) {
    return formatQuotedContextLabel(firstQuotedContext);
  }
  return "Queued follow-up";
}

export interface QueuedComposerTurnsSummary {
  total: number;
  autoSend: number;
  label: string;
}

export function summarizeQueuedComposerTurns(
  items: ReadonlyArray<ComposerQueuedMessage>,
): QueuedComposerTurnsSummary {
  const total = items.length;
  const autoSend = items.reduce((count, item) => (item.autoSend ? count + 1 : count), 0);
  const queuedLabel = `${total} queued`;
  return {
    total,
    autoSend,
    label: autoSend > 0 ? `${queuedLabel} · ${autoSend} auto-send` : queuedLabel,
  };
}

/**
 * Ctrl+S (and Cmd+S on a Mac) saves the composer draft to the queue.
 *
 * Cmd+S is accepted too, even though Ctrl is the documented chord: on a Mac it would otherwise
 * hand the browser its "save page" dialog, which is never what someone typing in a composer meant.
 */
export function isSaveComposerToQueueShortcut(event: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}): boolean {
  if (event.key.toLowerCase() !== "s") return false;
  if (event.altKey || event.shiftKey) return false;
  // Exactly one of the two, so Ctrl+Cmd+S stays free for whatever the OS wants with it.
  return event.ctrlKey !== event.metaKey;
}

/** Everything a chat message needs to become a queued row. */
export interface ComposerQueueChatSource {
  kind: "chat";
  /** Raw composer text, placeholders included: this is what "Edit" restores. */
  prompt: string;
  /** Result of `deriveComposerSendState().trimmedPrompt`. */
  trimmedPrompt: string;
  /** Already read as data URLs by the caller — the queue row owns the bytes from here on. */
  attachments: ReadonlyArray<UploadChatAttachment>;
  quotedContexts: ReadonlyArray<QuotedContextDraft>;
  skills: ReadonlyArray<ProviderSkillReference>;
  mentions: ReadonlyArray<ProviderMentionReference>;
  providerPlugins: ReadonlyArray<ComposerPluginSuggestion>;
  envMode: "local" | "worktree";
  interactionMode: ProviderInteractionMode;
}

/** Everything a plan follow-up needs. It never carries attachments or quotes. */
export interface ComposerQueuePlanFollowUpSource {
  kind: "plan-follow-up";
  /** Already resolved by `resolvePlanFollowUpSubmission`. */
  text: string;
  interactionMode: "default" | "plan";
  sourceProposedPlan?: SourceProposedPlanReference | undefined;
}

export function buildComposerQueueEnqueueInput(input: {
  threadId: ThreadIdType;
  source: ComposerQueueChatSource | ComposerQueuePlanFollowUpSource;
  selectedProvider: ProviderKind;
  selectedModel: string | null;
  selectedPromptEffort: string | null;
  modelSelection: ModelSelection;
  providerOptions?: ProviderStartOptions | undefined;
  assistantDeliveryMode: AssistantDeliveryMode;
  runtimeMode: RuntimeMode;
  autoSend?: boolean;
}): ComposerQueueEnqueueInput {
  const { source } = input;
  const common = {
    modelSelection: input.modelSelection,
    ...(input.providerOptions ? { providerOptions: input.providerOptions } : {}),
    assistantDeliveryMode: input.assistantDeliveryMode,
    runtimeMode: input.runtimeMode,
  };

  if (source.kind === "plan-follow-up") {
    const text = formatOutgoingPrompt({
      provider: input.selectedProvider,
      model: input.selectedModel,
      effort: input.selectedPromptEffort,
      text: source.text.trim(),
    });
    return {
      threadId: input.threadId,
      kind: "plan-follow-up",
      payload: {
        previewText: source.text.trim(),
        turn: {
          text,
          ...common,
          interactionMode: source.interactionMode,
          ...(source.sourceProposedPlan ? { sourceProposedPlan: source.sourceProposedPlan } : {}),
        },
        composer: {
          kind: "plan-follow-up",
          text: source.text,
          selectedProvider: input.selectedProvider,
          selectedModel: input.selectedModel,
          selectedPromptEffort: input.selectedPromptEffort,
        },
      },
      attachments: [],
      ...(input.autoSend === undefined ? {} : { autoSend: input.autoSend }),
    };
  }

  // Same pipeline the live send uses, in the same order: quotes are folded into the prompt first,
  // then the effort prefix, and only then are skills and mentions matched against the final text.
  const messageText = appendQuotedContextsToPrompt(source.prompt, source.quotedContexts);
  const text = formatOutgoingPrompt({
    provider: input.selectedProvider,
    model: input.selectedModel,
    effort: input.selectedPromptEffort,
    text: messageText || attachmentOnlyBootstrapPrompt(source.attachments),
  });
  const mentionedSkills = source.skills.filter((skill) =>
    promptIncludesSkillMention(text, skill.name, input.selectedProvider),
  );
  const mentionedPlugins = resolvePromptPluginMentions({
    prompt: text,
    existingMentions: source.mentions,
    providerPlugins: source.providerPlugins,
  });

  return {
    threadId: input.threadId,
    kind: "chat",
    payload: {
      previewText: buildQueuedComposerPreviewText({
        trimmedPrompt: source.trimmedPrompt,
        attachments: source.attachments,
        quotedContexts: source.quotedContexts,
      }),
      turn: {
        text,
        ...(mentionedSkills.length > 0 ? { skills: mentionedSkills } : {}),
        ...(mentionedPlugins.length > 0 ? { mentions: mentionedPlugins } : {}),
        ...common,
        interactionMode: source.interactionMode,
      },
      composer: {
        kind: "chat",
        prompt: source.prompt,
        quotedContexts: source.quotedContexts,
        skills: source.skills,
        mentions: source.mentions,
        selectedProvider: input.selectedProvider,
        selectedModel: input.selectedModel,
        selectedPromptEffort: input.selectedPromptEffort,
        envMode: source.envMode,
      },
    },
    attachments: source.attachments,
    ...(input.autoSend === undefined ? {} : { autoSend: input.autoSend }),
  };
}
